import React, { useCallback, useEffect, useContext, lazy, Suspense } from 'react';
import {
    AuthContextProvider,
    ContactSupportScreen,
    ReactRouterAuthGuard,
    ReactRouterGuestGuard,
    ForgotPasswordScreen,
    RegistrationContextProvider,
    ResetPasswordScreen,
    OktaRedirectLoginScreen,
    OktaAuthContextProvider,
    RegistrationWorkflow,
    EulaScreen,
    CreateAccountScreen,
    CreatePasswordScreen,
    VerifyCodeScreen,
    WorkflowCardHeaderProps,
    i18nAppInstance
} from '@brightlayer-ui/react-auth-workflow';
import { useNavigate } from 'react-router';
import { ProjectAuthUIActions } from '../actions/AuthUIActions';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { ProjectRegistrationUIActions } from '../actions/RegistrationUIActions';
import { routes } from './Routing';
import utilityCloudi18n from '../i18n';
import { useKeycloak } from '../contexts/KeycloakContext';
import { useOktaAuth, LoginCallback } from '@okta/okta-react';
import { CustomDetailsScreen, CustomDetailsScreenTwo } from '../components/CustomDetailsScreen';
import { SAMPLE_EULA } from '../constants/sampleEula';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { StateContext } from '../contexts/StateContext';
import { ChangePasswordScreeen } from '../pages/ChangePasswordScreen';
import { DTPG_NAVIGATION_MENU } from '../menuMetaData';
import { LINEPRO_NAVIGATION_MENU } from '../falconMenuMetaData';
import { EDGE_PLATFORM_NAVIGATION_MENU } from '../router/menuRoutes';

import ReactHome from '../pages/ReactHome/ReactHome';
import EmailFirstPasswordReset from '../components/EmailFirstPasswordReset';
import { I18nextProvider } from 'react-i18next';
import { PortalClientStorage } from '../services/client-storage/storage';
import { PortalApiToken } from '../services/api/api-token';
import { Typography } from '@mui/material';
import supersetConfig from '../supersetConfig';
import { transform } from '../router/NavigationMenu';
import { environment } from '../environments/environment';
import axios from 'axios';
import { AUTH_MODES } from '../utils/authMode';
import { isDevPortalEnvironment } from '../utils/deploymentEnvironment';
import DlmLandingComponent from '../pages/DLM/DlmLandingComponent';
import NewJobOverview from '../pages/DLM/NewJobOverview';


//lazy loaded imports
const PageOne = lazy(() => import('../pages/pageOne'));
const SupersetDashboard = lazy(() => import('../components/common/SupersetDasbhoard'));
const CircularProgress = lazy(() => import('@mui/material/CircularProgress'));
const OperationalAnalyticsHome = lazy(() => import('../pages/OperationalAnalytics/OperationalAnalyticsHome'));
const ProductHomeComponent = lazy(() => import('../pages/Products/ProductHomeComponent'));
const POUsers = lazy(() => import('../pages/ProductOwner/Users/Users'));
const PendingApprovals = lazy(() => import('../pages/ProductOwner/PendingApprovals/PendingApprovals'));
const ActivityLog = lazy(() => import('../pages/OperationalAnalytics/ActivityLog/ActivityLog'));
const PageThree = lazy(() => import('../pages/pageThree'));
const UploadFile = lazy(() => import('../pages/SmpFileAnalytics/UploadFile'));
const AutoApprovalComponent = lazy(() => import('../pages/ProductOwner/AutoApproval/AutoApprovalComponent'));
const DeviceRegisteration = lazy(() => import('../pages/FactoryReset/DeviceRegisteration'));
const AllFiles = lazy(() => import('../pages/SmpFileAnalytics/AllFiles'));
const RegisterCustomer = lazy(() => import('../pages/FactoryReset/RegisterCustomer'));
const RegisterCustomerSite = lazy(() => import('../pages/FactoryReset/RegisterCustomerSite'));
const FindDeviceByRegKey = lazy(() => import('../pages/FactoryReset/FindDeviceRegKey'));
const ListDevices = lazy(() => import('../pages/FactoryReset/ListDevices'));
const FindDeviceByType = lazy(() => import('../pages/FactoryReset/FindDeviceByType'));
const FADRActivityLog = lazy(() => import('../pages/FactoryReset/ActivityLog/FADRActivityLog'));
const AllRules = lazy(() => import('../pages/SmpFileAnalytics/Rules/AllRules'));
const MyRules = lazy(() => import('../pages/SmpFileAnalytics/Rules/MyRules'));
const ShareWithMe = lazy(() => import('../pages/SmpFileAnalytics/Rules/ShareWithMe'));
const MyFiles = lazy(() => import('../pages/SmpFileAnalytics/MyFiles'));
const PageNotFound = lazy(() => import('../pages/PageNotFound/pageNotFound'));
const ViewProductComponent = lazy(() => import('../pages/Products/ViewProductComponent'));
const AppWrapper = lazy(() => import('../pages/App-Wrapper/app-wrapper-component'));
const YukonSystems = lazy(() => import('../pages/YukonSystems/YukonSystems'));
const SliMetricDetail = lazy(() => import('../pages/YukonSystems/SliMetricDetail'));
const MetricsDataflowViewer = lazy(() => import('../pages/YukonSystems/MetricsDataflowViewer'));
const MetricsExplorer = lazy(() => import('../pages/YukonSystems/MetricsExplorer'));
const NotificationsPage = lazy(() => import('../pages/YukonSystems/NotificationsPage'));
const AlarmSilencingPage = lazy(() => import('../pages/YukonSystems/AlarmSilencingPage'));
const NotificationDetail = lazy(() => import('../pages/YukonSystems/NotificationDetail'));
const DrEventOverviewPage = lazy(() => import('../pages/DrEventOverview/DrEventOverviewPage'));
const DrEventReportDetail = lazy(() => import('../pages/DrEventOverview/DrEventReportDetail'));
const DrAnalyserOverviewPage = lazy(() => import('../pages/DrAnalyserOverview/DrAnalyserOverviewPage'));
const DrAnalyserSnapshotDetail = lazy(() => import('../pages/DrAnalyserOverview/DrAnalyserSnapshotDetail'));
const LicenseManagement = lazy(() => import('../pages/ProductOwner/LicenseManagement/LicenseManagement'));
const UserLicenses = lazy(() => import('../pages/UserAccount/UserLicenses'));


/**
 * LoginRedirect Component
 * 
 * Purpose: Immediately trigger authentication without showing any UI
 * Used for Keycloak mode where we want to start the auth flow automatically
 * 
 * For Okta mode: This component triggers signInWithRedirect
 * For Keycloak mode: This component triggers the email-first login flow
 * 
 * Returns null to avoid any UI flash during redirect
 */
const LoginRedirect = ({ authMode, oktaAuth, keycloakLogin }) => {
    useEffect(() => {
        if (authMode === AUTH_MODES.OKTA && oktaAuth) {
            // Add a small delay to ensure page is fully loaded
            setTimeout(() => {
                try {
                    const redirectPromise = oktaAuth.signInWithRedirect({ originalUri: '/products/all' });
                    
                    if (redirectPromise && redirectPromise.then) {
                        redirectPromise
                            .then(() => {
                                // Redirect should happen now
                            })
                            .catch(err => {
                                // Show user-friendly error
                                alert('Cannot connect to authentication server. Please check your network connection or contact support.');
                            });
                    }
                } catch (error) {
                    alert('Authentication error. Please refresh the page and try again.');
                }
            }, 100);
        } else if (authMode === AUTH_MODES.KEYCLOAK && keycloakLogin) {
            keycloakLogin();
        }
    }, [authMode, oktaAuth, keycloakLogin]);

    return null; // No UI to render
};


const RouterWrapper = ({ SharedLayoutWrapper, authMode = AUTH_MODES.OKTA }) => {



    //Auth states accessed
    const { setIsAuthenticated } = useContext(StateContext);
    const { loginData, setLoginData } = useContext(StateContext);
    const { setAccessToken } = useContext(StateContext);
    const { setAvatarText } = useContext(StateContext);
    const { setName } = useContext(StateContext);
    const { setEmail } = useContext(StateContext);
    const { language } = useContext(StateContext);
    const { showChangePasswordDailog } = useContext(StateContext);

    const navigation = useNavigate();
    const { t } = useTranslation();
    const navigate = useCallback((destination) => {
        navigation(destination);
    }, [navigation]);

    const currentUserData = PortalClientStorage.getData('currentUser', true);
    const currentUserRoles = currentUserData ? JSON.parse(currentUserData) : [];
    const hasDevicesManagementAdminRole = currentUserRoles?.includes('DevicesManagementAdmin');
    const isDtpgUser = currentUserRoles?.includes('DTPGUser');
    const isFalconUser = currentUserRoles?.includes('NovaLineProUser') || currentUserRoles?.includes('c4c5e67d-ed10-e9fb-a750-f9a6bc2eaaa7');
    const massOtaEnabled = currentUserData ? JSON.parse(currentUserData)?.includes("OTADLMAdmin") : false;
    const hasYukonSystemsRole = currentUserData
        ? JSON.parse(currentUserData)?.includes('YukonSupport')
        : false;
    const isDevEnvironment = isDevPortalEnvironment();
    const hasYukonSystemsAccess = hasYukonSystemsRole && isDevEnvironment;
    const hasDrEventOverviewRole = massOtaEnabled && isDevEnvironment;
    const hasNotificationsRole = (hasYukonSystemsRole || massOtaEnabled) && isDevEnvironment;
    /**
     * DUAL AUTHENTICATION SYSTEM
     * 
     * This app supports both Okta and Keycloak authentication providers.
     * The active provider is determined by the authMode prop (from AppRouter).
     * 
     * IMPORTANT: Both hooks must be called unconditionally (React Hooks rules).
     * Each hook returns safe defaults when its provider is not present.
     */
    const keycloakAuth = useKeycloak();
    const oktaAuthState = useOktaAuth();

    /**
     * Normalize authentication state from both providers
     * Extract the relevant data based on current authMode
     */
    const authenticated = authMode === AUTH_MODES.OKTA
        ? oktaAuthState?.authState?.isAuthenticated || false
        : keycloakAuth?.authenticated || false;

    const token = authMode === AUTH_MODES.OKTA
        ? oktaAuthState?.authState?.accessToken?.accessToken
        : keycloakAuth?.token;

    const loading = authMode === AUTH_MODES.OKTA
        ? !oktaAuthState?.authState
        : keycloakAuth?.loading || false;

    const completeBackendAuth = keycloakAuth?.completeBackendAuth;
    const keycloakLoading = authMode === AUTH_MODES.KEYCLOAK ? loading : false;

    const baseURL = environment.baseURL;

    /**
     * Backend Authentication Setup
     * 
     * After the user authenticates with Okta or Keycloak, we need to:
     * 1. Exchange the OAuth token for a BUC-specific token
     * 2. Retrieve user profile data from BUC backend
     * 3. Store tokens and user data in local storage
     * 4. Initialize API token for subsequent requests
     * 
     * This effect runs when:
     * - User successfully authenticates (authenticated = true)
     * - OAuth token is available (token is set)
     * - Runs for BOTH Okta and Keycloak modes
     */
    useEffect(() => {
        if (authenticated && token) {
            const loginResponse = axios.get(`${baseURL}/security/login`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'X-Auth-Provider': authMode, // Help backend identify auth provider
                },
            });

            loginResponse
                .then((data) => {
                    const userId = data?.data?.data?.id;
                    const bucToken = data?.data?.data?.token;
                    const role = data?.data?.data?.userRole;
                    const displayName = data?.data?.data?.displayName;
                    const userEmail = data?.data?.data?.email;
                    
                    // Use email as fallback if displayName is null or empty
                    const effectiveDisplayName = displayName || userEmail || 'User';
                    
                    const userDetails = {
                        name: effectiveDisplayName,
                        email: userEmail,
                        rememberMe: false,
                    };
                    
                    // CRITICAL: Initialize API token with BUC token FIRST before setting any state
                    // This ensures all subsequent API calls use the BUC token
                    PortalApiToken.initializeApiToken({
                        accessToken: bucToken,
                        refreshToken: data?.data?.data?.refreshToken,
                    });
                    
                    // Store user data in client storage
                    PortalClientStorage.setData('userId', userId, true);
                    PortalClientStorage.setData('currentUser', JSON.stringify(role), true);
                    PortalClientStorage.setData('userDetails', JSON.stringify(userDetails), true);
                    
                    // Set avatar text from display name (with fallback)
                    setAvatarText(transform(effectiveDisplayName));
                    setName(effectiveDisplayName);
                    setEmail(userEmail);
                    
                    // Store user data for legacy compatibility
                    const userData = {
                        user: userEmail,
                        userId: userEmail,
                    };
                    PortalClientStorage.setData('user_data', JSON.stringify(userData), true);
                    
                    setLoginData({
                        email: userEmail,
                        rememberMe: false,
                    });
                    
                    // Set authentication state AFTER all token setup completes
                    setIsAuthenticated(true);
                    setAccessToken(bucToken);
                    
                    // Clear the loading state in KeycloakContext now that backend auth is complete
                    if (authMode === AUTH_MODES.KEYCLOAK && completeBackendAuth) {
                        completeBackendAuth();
                    }
                })
                .catch((err) => {
                    console.error(`❌ Error calling backend security/login with ${authMode}:`, err);
                    // On error, still set authenticated to avoid getting stuck
                    setIsAuthenticated(false);

                    // Sign out of the OIDC provider so `authenticated` flips back to false.
                    // Without this, `backendAuthInProgress` (authenticated && !isAuthenticated)
                    // would remain true and the loading spinner would hang indefinitely.
                    // `signOut()` / `logout()` may return a promise; wrap in Promise.resolve so
                    // an async rejection is captured instead of surfacing as an unhandled rejection.
                    if (authMode === AUTH_MODES.OKTA) {
                        Promise.resolve()
                            .then(() => oktaAuthState?.oktaAuth?.signOut?.())
                            .catch((signOutErr) => console.error('Error signing out of Okta after backend failure:', signOutErr));
                    } else if (authMode === AUTH_MODES.KEYCLOAK) {
                        Promise.resolve()
                            .then(() => keycloakAuth?.logout?.())
                            .catch((signOutErr) => console.error('Error signing out of Keycloak after backend failure:', signOutErr));
                    }

                    // Clear loading state even on error
                    if (authMode === AUTH_MODES.KEYCLOAK && completeBackendAuth) {
                        completeBackendAuth();
                    }
                });
        }
    }, [authenticated, token, authMode, baseURL, setIsAuthenticated, setAccessToken, setAvatarText, setName, setEmail, setLoginData, completeBackendAuth]);

   

    // Use StateContext's isAuthenticated (set after backend call completes)
    const { isAuthenticated: stateIsAuthenticated } = useContext(StateContext);
    const isAuthenticated = stateIsAuthenticated;

    /**
     * Backend Authentication Gap
     *
     * Between the moment the OIDC provider (Okta / Keycloak) reports the user
     * as authenticated and the moment our BUC `/security/login` exchange
     * finishes setting `isAuthenticated` in StateContext, the router would
     * otherwise fall through to the guest branch and briefly render
     * `<ReactHome />` (the public welcome page).
     *
     * This derived flag is true exactly for that window and is used below to
     * render a loading spinner instead of `<ReactHome />`. It works uniformly
     * for both Okta and Keycloak modes (Keycloak's own `loading` flag covers
     * the pre-OIDC init phase; this covers the post-OIDC backend exchange).
     */
    const backendAuthInProgress = authenticated && !isAuthenticated;


    const workflowCardHeaderProps = {
        title: t('translation:pages.router_wrapper.titleAccountDetails'),
        ...WorkflowCardHeaderProps,
    };

    const workflowCardHeaderPropsForScreenTwo = {
        title: t('translation:pages.router_wrapper.titleJobInfo'),
        ...WorkflowCardHeaderProps,
    };

    const customDetailsScreenOneTranslations = {
        next: t('translation:pages.router_wrapper.custom_details_Screen_one.next'),
        back: t('translation:pages.router_wrapper.custom_details_Screen_one.back'),
        instructions: t('translation:pages.router_wrapper.custom_details_Screen_one.instruction'),
        firstNameLabel: t('translation:pages.router_wrapper.custom_details_Screen_one.firstNameLabel'),
        lastNameLabel: t('translation:pages.router_wrapper.custom_details_Screen_one.lastNameLabel'),
        phoneLabel: t('translation:pages.router_wrapper.custom_details_Screen_one.phoneLabel'),
        phonePlaceholder: t('translation:pages.router_wrapper.custom_details_Screen_one.phoneNumberPlaceHolder'),
        countryLabel: t('translation:pages.router_wrapper.custom_details_Screen_one.countryLabel'),
        regionLabel: t('translation:pages.router_wrapper.custom_details_Screen_one.regionLabel'),
    };

    const customDetailsScreenTwoTranslations = {
        next: t('translation:pages.router_wrapper.custom_details_Screen_two.next'),
        back: t('translation:pages.router_wrapper.custom_details_Screen_two.back'),
        instruction: t('translation:pages.router_wrapper.custom_details_Screen_two.instruction'),
        orgLabel: t('translation:pages.router_wrapper.custom_details_Screen_two.orgLabel'),
        orgPlaceholder: t('translation:pages.router_wrapper.custom_details_Screen_two.orgPlaceholder'),
        electricUtilityLabel: t('translation:pages.router_wrapper.custom_details_Screen_two.electricUtilityLabel'),
        electricUtilityPlaceholder: t('translation:pages.router_wrapper.custom_details_Screen_two.electricUtilityPlaceholder'),
        jobTitle: t('translation:pages.router_wrapper.custom_details_Screen_two.jobTitle'),
        jobTitlePlaceholder: t('translation:pages.router_wrapper.custom_details_Screen_two.jobTitlePlaceholder'),
    };

    const openEulaInNewTab = () => {
        window.open('/eula.htm', '_blank');
    };

    return (
        <Suspense fallback={<CircularProgress />}>
            <Routes>
                <Route
                    element={
                        <AuthContextProvider
                            actions={ProjectAuthUIActions()}
                            language={language}
                            navigate={navigate}
                            routeConfig={routes}
                            i18n={i18nAppInstance}
                            rememberMeDetails={{
                                email: loginData?.rememberMe ? loginData.email : '',
                                rememberMe: loginData?.rememberMe,
                            }}
                        >
                            <Outlet />
                            {showChangePasswordDailog && <ChangePasswordScreeen />}
                        </AuthContextProvider>
                    }
                >
                    <Route
                        path={'/login'}
                        element={
                            <ReactRouterGuestGuard isAuthenticated={isAuthenticated} fallBackUrl={'/products/all'}>
                                {authMode === AUTH_MODES.OKTA ? (
                                    <OktaAuthContextProvider
                                        language={language}
                                        navigate={navigate}
                                        routeConfig={routes}
                                        i18n={i18nAppInstance}
                                    >
                                        <OktaRedirectLoginScreen
                                            loginButtonLabel={t('translation:pages.router.log_in')}
                                            contactSupportLabel={t('translation:pages.router.terms_and_condition')}
                                            forgotPasswordLabel={t('translation:pages.router.forgot_your_password')}
                                            selfRegisterButtonLabel={t('translation:pages.router.create_account')}
                                            selfRegisterInstructions={t('translation:pages.router.need_an_account')}
                                            onContactSupport={openEulaInNewTab}
                                        />
                                    </OktaAuthContextProvider>
                                ) : (
                                    <LoginRedirect
                                        authMode={authMode}
                                        oktaAuth={oktaAuthState?.oktaAuth}
                                        keycloakLogin={keycloakAuth?.login}
                                    />
                                )}
                            </ReactRouterGuestGuard>
                        }
                    />
                    <Route path={'/login/callback'} element={<LoginCallback />} />
                    <Route
                        path={'/forgot-password'}
                        element={
                            <ReactRouterGuestGuard fallBackUrl={'/'}>
                                {authMode === AUTH_MODES.OKTA ? (
                                    <ForgotPasswordScreen
                                        WorkflowCardHeaderProps={{
                                            title: `${t('translation:pages.router_wrapper.resetPassword')}`,
                                        }}
                                        slotProps={{
                                            SuccessScreen: {
                                                WorkflowCardHeaderProps: {
                                                    title: `${t('translation:pages.router_wrapper.resetPassword')}`,
                                                },
                                                EmptyStateProps: {
                                                    title: (
                                                        <h6>
                                                            {t(
                                                                'translation:pages.router_wrapper.forgot_password.emailSent'
                                                            )}
                                                        </h6>
                                                    ),
                                                    description: (
                                                        <div>
                                                            {t(
                                                                'translation:pages.router_wrapper.forgot_password.description'
                                                            )}
                                                        </div>
                                                    ),
                                                },
                                                dismissButtonLabel: t(
                                                    'translation:pages.router_wrapper.forgot_password.doneButton'
                                                ),
                                            },
                                        }}
                                        emailLabel={`${t('translation:pages.router.email')}`}
                                        WorkflowCardInstructionProps={{
                                            instructions: (
                                                <Typography sx={{ px: { md: 3, xs: 2 }, pt: 2 }}>
                                                    {t('translation:pages.router_wrapper.instructionOne')}
                                                    <br />
                                                    <br />
                                                    {t('translation:pages.router_wrapper.instructionTwo')}
                                                </Typography>
                                            ),
                                        }}
                                        WorkflowCardActionsProps={{
                                            showPrevious: true,
                                            previousLabel: t('translation:pages.router.back'),
                                            showNext: true,
                                            nextLabel: t('translation:pages.router.submit'),
                                            canGoNext: true,
                                            fullWidthButton: true,
                                        }}
                                    />
                                ) : (
                                    <EmailFirstPasswordReset />
                                )}
                            </ReactRouterGuestGuard>
                        }
                    />
                    <Route
                        path={'/contact-support'}
                        element={
                            <ReactRouterGuestGuard isAuthenticated={isAuthenticated} fallBackUrl={'/'}>
                                <ContactSupportScreen
                                    contactEmail={'eas-support@eaton.com'}
                                    contactPhone={'(800) 815-2285'}
                                />
                            </ReactRouterGuestGuard>
                        }
                    />
                    <Route
                        path={'/reset-password'}
                        element={
                            <ReactRouterGuestGuard isAuthenticated={isAuthenticated} fallBackUrl={'/'}>
                                <ResetPasswordScreen />
                            </ReactRouterGuestGuard>
                        }
                    />
                    <Route
                        path={'/resetpassword'}
                        element={
                            <ReactRouterGuestGuard isAuthenticated={isAuthenticated} fallBackUrl={'/'}>
                                <ResetPasswordScreen />
                            </ReactRouterGuestGuard>
                        }
                    />

                    <Route
                        path={'/assets/api-terms.pdf'}
                        element={
                            <ReactRouterGuestGuard isAuthenticated={isAuthenticated} fallBackUrl={'/products/all'}>
                                <Navigate to="/assets/api-terms.pdf" replace />
                            </ReactRouterGuestGuard>
                        }
                    />

                    {/* Grid solutions routes placed below */}

                    {isAuthenticated ? (
                        <Route
                            key="auth-routes"
                            path={''}
                            element={
                                <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                    <SharedLayoutWrapper />
                                </ReactRouterAuthGuard>
                            }
                        >
                            <Route
                                path={''}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <ProductHomeComponent />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/products/all'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <ProductHomeComponent />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/products/:id'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <ViewProductComponent />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            {massOtaEnabled && <Route
                                path={'/dlm'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <DlmLandingComponent />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />}

                            {massOtaEnabled && <Route
                                path={'/dlm/new-job'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <NewJobOverview />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />}

                            {hasDrEventOverviewRole && <Route
                                path={'/asset-management/dr-events'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <DrEventOverviewPage hasDevicesManagementAdminRole={hasDevicesManagementAdminRole} />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />}

                            {hasDrEventOverviewRole && <Route
                                path={'/asset-management/dr-analyser'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <DrAnalyserOverviewPage />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />}

                            {hasDrEventOverviewRole && <Route
                                path={'/asset-management/dr-analyser/snapshots/:snapshotId'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <DrAnalyserSnapshotDetail />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />}
                            {hasYukonSystemsAccess && <Route
                                path={'/yukon-systems'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <YukonSystems />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />}

                            {hasYukonSystemsAccess && <Route
                                path={'/yukon-systems/:siteId/sli/:sliId'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <SliMetricDetail />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />}

                            {hasYukonSystemsAccess && <Route
                                path={'/yukon-systems/metrics-diagrams'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <MetricsDataflowViewer />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />}

                            {hasYukonSystemsAccess && <Route
                                path={'/yukon-systems/metrics'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <MetricsExplorer />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />}

                            {hasNotificationsRole && <Route
                                path={'/yukon-systems/notifications'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <NotificationsPage />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />}

                            {hasNotificationsRole && <Route
                                path={'/yukon-systems/notifications/alarm-silencing'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <AlarmSilencingPage />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />}

                            {hasNotificationsRole && <Route
                                path={'/yukon-systems/notifications/:siteId/:notificationId'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <NotificationDetail />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />}

                            {hasDrEventOverviewRole && <Route
                                path={'/asset-management/dr-events/reports/:reportId'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <DrEventReportDetail />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />}

                            <Route
                                path={'/analysis'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <OperationalAnalyticsHome />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/analysis-appevents'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <ActivityLog />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/productowner/appevents'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <PageOne />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/productowner/license-management'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <LicenseManagement />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                             <Route
                                path={'/license-management'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <UserLicenses />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/productowner/pending-approval'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <PendingApprovals />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />
                            <Route
                                path={'/fam/problem-details/:id'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <SupersetDashboard
                                                showFilter="true"
                                                supersetUrl={supersetConfig.superset_url}
                                                fullscreen="true"
                                                rlsColumn="problemId"
                                                dashboardId={supersetConfig.dashboard_fam_problem_details_id}
                                                pageHeaderKey="translation:pageHeader.fam_problem_details"
                                            />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/fam/problems'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <SupersetDashboard
                                                showFilter="true"
                                                supersetUrl={supersetConfig.superset_url}
                                                dashboardId={supersetConfig.dashboard_fam_problems_id}
                                                pageHeaderKey="translation:pageHeader.fam_problems"
                                            />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />
                            <Route
                                path={'/fam/problemsback'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <SupersetDashboard
                                                showFilter="true"
                                                supersetUrl={supersetConfig.superset_url}
                                                fullscreen="true"
                                                dashboardId={supersetConfig.dashboard_fam_problems_id}
                                                pageHeaderKey="translation:pageHeader.fam_problems"
                                            />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/fam/events'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <SupersetDashboard
                                                supersetUrl={supersetConfig.superset_url}
                                                dashboardId={supersetConfig.dashboard_fam_events_id}
                                                pageHeaderKey="translation:pageHeader.fam_events"
                                            />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />
                            <Route
                                path={'/fam/eventsback'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <SupersetDashboard
                                                showFilter="true"
                                                supersetUrl={supersetConfig.superset_url}
                                                fullscreen="true"
                                                dashboardId={supersetConfig.dashboard_fam_events_id}
                                                pageHeaderKey="translation:pageHeader.fam_events"
                                            />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />
                            <Route
                                path={'/fam/event-details/:id'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <SupersetDashboard
                                                showFilter="true"
                                                supersetUrl={supersetConfig.superset_url}
                                                fullscreen="true"
                                                rlsColumn="id"
                                                dashboardId={supersetConfig.dashboard_fam_event_details_id}
                                                pageHeaderKey="translation:pageHeader.fam_event_details"
                                            />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/fam/devices'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <SupersetDashboard
                                                supersetUrl={supersetConfig.superset_url}
                                                dashboardId={supersetConfig.dashboard_fam_devices_id}
                                                pageHeaderKey="translation:pageHeader.fam_devices"
                                            />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />
                            <Route
                                path={'/fam/devicesback'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <SupersetDashboard
                                                showFilter="true"
                                                supersetUrl={supersetConfig.superset_url}
                                                fullscreen="true"
                                                dashboardId={supersetConfig.dashboard_fam_devices_id}
                                                pageHeaderKey="translation:pageHeader.fam_devices"
                                            />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />
                            <Route
                                path={'/fam/device-details/:id'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <SupersetDashboard
                                                showFilter="true"
                                                supersetUrl={supersetConfig.superset_url}
                                                fullscreen="true"
                                                rlsColumn="Name"
                                                dashboardId={supersetConfig.dashboard_fam_device_details_id}
                                                pageHeaderKey="translation:pageHeader.fam_device_details"
                                            />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />
                            <Route
                                path={'/lcrsmanagement/communications'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <SupersetDashboard
                                                supersetUrl={supersetConfig.superset_url}
                                                dashboardId="bcbb5b07-3b76-4643-95d6-2aec52e85d4e"
                                                pageHeaderKey="translation:pageHeader.lcr_communications_dashboard"
                                            />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/lcrsmanagement/firmware'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <SupersetDashboard
                                                supersetUrl={supersetConfig.superset_url}
                                                dashboardId="4b1f3273-3748-4ffd-a1a7-286e995539e1"
                                                pageHeaderKey="translation:pageHeader.lcr_firmware_dashboard"
                                            />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/lcrsmanagement/market'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <SupersetDashboard
                                                supersetUrl={supersetConfig.superset_url}
                                                dashboardId="21044d61-fb48-4149-b648-914518e83b8e"
                                                pageHeaderKey="translation:pageHeader.lcr_admin_market_dashboard"
                                            />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/lcrsmanagement/device-details/:id'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <SupersetDashboard
                                                supersetUrl={supersetConfig.superset_url}
                                                rlsColumn="deviceId"
                                                dashboardId="92daa873-0b72-48c1-a17a-fe7841e8f25a"
                                                pageHeaderKey="translation:pageHeader.lcr_device_details"
                                            />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/productowner/productsusers'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <POUsers />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/productowner/customers'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <PageThree />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/analytics/uploadfiles'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <UploadFile />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/productowner/product_autoapproval'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <AutoApprovalComponent />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/factoryreset/device-registration'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <DeviceRegisteration />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/analytics/reports'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <AllFiles />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/factoryreset/register-customer'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <RegisterCustomer />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/factoryreset/register-customer-site'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <RegisterCustomerSite />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/factoryreset/find-device-key-by-registration'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <FindDeviceByRegKey />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/factoryreset/list-devices'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <ListDevices />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/factoryreset/find-device-by-type-and-number'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <FindDeviceByType />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/factoryreset/fadr-appevents'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <FADRActivityLog />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            {EDGE_PLATFORM_NAVIGATION_MENU?.map((menuItem) => (
                                <Route
                                    key={`edge-${menuItem.link}`}
                                    path={menuItem.link}
                                    element={
                                        <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                            <AppWrapper
                                                dtpgSrc="../../../maestro-web-app"
                                                pageHeader={t('translation:menu.edge_platform.edgemanager')}
                                            />
                                        </ReactRouterAuthGuard>
                                    }
                                />
                            ))}

                            {isFalconUser
                                ? LINEPRO_NAVIGATION_MENU?.map((menuItem) => (
                                      <Route
                                          key={`falcon-${menuItem.link}`}
                                          path={menuItem.link}
                                          element={
                                              <ReactRouterAuthGuard
                                                  isAuthenticated={isAuthenticated}
                                                  fallBackUrl={'/login'}
                                              >
                                                  <AppWrapper
                                                      dtpgSrc="../../../falcon-web-app"
                                                      pageHeader={t('translation:menu.LINEPRO')}
                                                  />
                                              </ReactRouterAuthGuard>
                                          }
                                      />
                                  ))
                                : null}

                            <Route
                                path={'/analytics/rules/shareWithMe'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <ShareWithMe />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            <Route
                                path={'/analytics/reports/my-files'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <MyFiles />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />

                            {isDtpgUser
                                ? DTPG_NAVIGATION_MENU?.map((menuItem) => (
                                      <Route
                                          key={`dtpg-${menuItem.link}`}
                                          path={menuItem.link}
                                          element={
                                              <ReactRouterAuthGuard
                                                  isAuthenticated={isAuthenticated}
                                                  fallBackUrl={'/login'}
                                              >
                                                  <Suspense fallback={<CircularProgress />}>
                                                      <AppWrapper
                                                          dtpgSrc="../../../dtpg-web-app"
                                                          pageHeader={t('translation:menu.DTPG')}
                                                      />
                                                  </Suspense>
                                              </ReactRouterAuthGuard>
                                          }
                                      />
                                  ))
                                : null}

                            <Route
                                path={'*'}
                                element={
                                    <ReactRouterAuthGuard isAuthenticated={isAuthenticated} fallBackUrl={'/login'}>
                                        <Suspense fallback={<CircularProgress />}>
                                            <PageNotFound />
                                        </Suspense>
                                    </ReactRouterAuthGuard>
                                }
                            />
                        </Route>
                    ) : (keycloakLoading || backendAuthInProgress) ? (
                        <Route
                            key="loading-routes"
                            path={'/'}
                            element={
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '100vh',
                                    }}
                                >
                                    <CircularProgress size={60} />
                                </div>
                            }
                        />
                    ) : (
                        <Route
                            key="guest-routes"
                            path={'/'}
                            element={
                                <ReactRouterGuestGuard isAuthenticated={isAuthenticated} fallBackUrl={'/products/all'}>
                                    <I18nextProvider i18n={utilityCloudi18n}>
                                        <ReactHome />
                                    </I18nextProvider>
                                </ReactRouterGuestGuard>
                            }
                        />
                    )}
                </Route>

                {/* REGISTRATION ROUTES */}
                <Route
                    element={
                        <RegistrationContextProvider
                            language={language}
                            routeConfig={routes}
                            navigate={navigate}
                            actions={ProjectRegistrationUIActions()}
                            i18n={i18nAppInstance}
                        >
                            <Outlet />
                        </RegistrationContextProvider>
                    }
                >
                    <Route
                        path={'/self-registration'}
                        element={
                            <RegistrationWorkflow>
                                <EulaScreen eulaContent={parse(SAMPLE_EULA)} />
                                <CreateAccountScreen />
                                <VerifyCodeScreen codeValidator={(para) => para.length > 0} />
                                <CreatePasswordScreen />
                                <CustomDetailsScreen
                                    WorkflowCardHeaderProps={workflowCardHeaderProps}
                                    translations={customDetailsScreenOneTranslations}
                                />
                                <CustomDetailsScreenTwo
                                    WorkflowCardHeaderProps={workflowCardHeaderPropsForScreenTwo}
                                    translations={customDetailsScreenTwoTranslations}
                                />
                            </RegistrationWorkflow>
                        }
                    />
                    <Route path={'/register-by-invite'} element={<RegistrationWorkflow isInviteRegistration />} />
                </Route>

                <Route path={'/logout/callback'} element={<Navigate to="/login" replace />} />
            </Routes>
        </Suspense>
    );

};


export default RouterWrapper;